import { type FC, useRef, forwardRef, type ChangeEvent, type MutableRefObject, type DragEvent } from 'react';

import classNames from 'classnames/bind';
import FAIcon from '../Icons/Icons';
import { type ImageEditorPropTypes } from './types/propsType';
import styles from './ImageEditor.module.scss';
import { ImageEditorSize } from './types/enums';

const cx = classNames.bind(styles);

/**
 *
 * @param file argument should a uploaded file
 * @returns process input file to readable Blob format URL
 */
const processFileToBlob = (file: File) => URL.createObjectURL(file);

const ImageEditor: FC<ImageEditorPropTypes> = forwardRef<HTMLDivElement, ImageEditorPropTypes>(
  (props: ImageEditorPropTypes, ref) => {
    const {
      maxFileCount,
      onFileUpload,
      imageEditorSize = styles[ImageEditorSize.INHERIT_SIZE],
      uploadCTALabel,
      dragCTALabel = 'Drag & Drop to Upload',
      connectorLabel = 'Or',
      ...restProps
    } = props;
    const inputFileRef = useRef() as MutableRefObject<HTMLInputElement>;
    const inputFileUploadClick = () => {
      inputFileRef.current.click();
    };
    const filterFiles = (files: FileList | null) => {
      let listFile: Record<number, File> = {};
      if (files) {
        listFile = { 0: files[0] as File };
        const limit = maxFileCount ?? Object.keys(files).length;
        if (restProps?.multiple) {
          for (let i = 1; i < limit; i++) {
            if (!files[i]) {
              break;
            }
            listFile[i] = files[i] as File;
          }
        }
      }
      return listFile;
    };
    const handleFileUploadChange = (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      onFileUpload?.({
        ...e,
        files: filterFiles(files),
      });
      inputFileRef.current.value = '';
    };
    const handleDrag = function (e: DragEvent) {
      e.preventDefault();
      e.stopPropagation();
    };
    const handleDrop = function (e: DragEvent) {
      handleDrag(e);
      onFileUpload?.({
        ...e,
        files: filterFiles(e.dataTransfer.files),
      });
    };

    const CClassname = cx('image-upload-container', { disabled: props?.disabled }, [imageEditorSize]);
    return (
      <section
        className={CClassname}
        onClick={inputFileUploadClick}
        onKeyDown={(e) => e.key === 'Enter' && inputFileUploadClick()}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onFocus={() => null}
        data-testid="image-editor-container-testid"
        role="presentation"
        ref={ref}
      >
        <FAIcon iconName="image" className={styles['file-upload-icon']} />
        <article className={styles['image-desc-text']}>
          <span className={styles['image-desc-text-desktop']}>
            {dragCTALabel && <span>{dragCTALabel}</span>}
            {connectorLabel && <span>{connectorLabel}</span>}
            <span className={styles['image-cta-label']}>{uploadCTALabel || 'Choose Image from Local Computer'}</span>
          </span>
          <span className={styles['mobile-image-cta-label']}>{uploadCTALabel || 'Choose Image from Local Device'}</span>
        </article>
        <input
          type="file"
          ref={inputFileRef}
          className={styles['input-image-upload']}
          onChange={handleFileUploadChange}
          data-testid="input-image-upload-testid"
          {...restProps}
        />
      </section>
    );
  },
);
ImageEditor.displayName = 'ImageEditor';
export { processFileToBlob };
export default ImageEditor;
